import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal, Select, SelectItemProps } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'

export default observer(
  ({
    item,
    onClose,
    onSave,
    trainingId,
    allowedContentTypes,
  }: {
    item?: ContentItem
    onClose: () => void
    onSave?: (item: ContentItem) => void
    trainingId: number
    allowedContentTypes: string[]
  }) => {
    const isEditing = !!item
    const [contentType, setContentType] = React.useState<string>(() =>
      item ? contentRegistry.getTypeStringForItem(item) || 'module' : 'module'
    )
    const [data, setData] = React.useState<ContentItem>(() =>
      item ? item.clone() : contentRegistry.createDefaultItem(contentType)
    )

    React.useEffect(() => {
      if (contentType && !isEditing) {
        setData((prevData) => {
          const newItem = contentRegistry.createDefaultItem(contentType)

          // Preserve shared properties when changing types (only if they have meaningful values)
          if ('title' in prevData && 'title' in newItem && prevData.title) {
            newItem.title = prevData.title
          }
          if ('description' in prevData && 'description' in newItem && prevData.description) {
            newItem.description = prevData.description
          }
          if (
            'thumbnail' in prevData &&
            'thumbnail' in newItem &&
            prevData.thumbnail &&
            !prevData.thumbnail.isEmpty()
          ) {
            newItem.thumbnail = prevData.thumbnail
          }

          return newItem
        })
      }
    }, [contentType, isEditing])

    React.useEffect(() => {
      const styleId = 'content-editor-select-dropdown-style'
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        [data-radix-popper-content-wrapper],
        [data-radix-popper-content-wrapper] > div,
        [data-radix-popper-content-wrapper] > div > div,
        [data-radix-popper-content-wrapper] div[class*="flex"][class*="col"] {
          padding-bottom: 2px !important;
        }
      `
      document.head.appendChild(style)

      return () => {
        document.getElementById(styleId)?.remove()
      }
    }, [])

    return (
      <OneModal isOpen onClose={onClose} position="center">
        <OneModal.Header
          title={
            isEditing
              ? i18n.t('trainings.content.content_modal.edit_title')
              : i18n.t('trainings.content.content_modal.create_title')
          }
        />
        <OneModal.Content withPadding>
          <Box className="gap-3">
            <Select
              size="md"
              label={i18n.t('trainings.content.content_modal.select_label')}
              onChange={setContentType}
              options={contentRegistry.getAll().map(
                (def): SelectItemProps<string> => ({
                  icon: def.icon,
                  label: def.label(),
                  description: def.description(),
                  value: def.typeString,
                  disabled: !allowedContentTypes.includes(def.typeString),
                })
              )}
              placeholder={i18n.t('trainings.content.content_modal.select_placeholder')}
              value={contentType}
              disabled={isEditing}
            />
            <Editor contentItem={data} trainingId={trainingId} />
          </Box>
        </OneModal.Content>
        <OneModal.Footer>
          <Box gap="s8" flexDirection="row" justifyContent="flexEnd" width="full">
            <F0Button label={i18n.t('common.cancel')} onClick={onClose} variant="outline" />
            <F0Button
              disabled={!(contentType && Object.keys(data.validate()).length === 0)}
              label={isEditing ? i18n.t('common.update') : i18n.t('common.save')}
              onClick={() => onSave?.(data)}
              variant="default"
            />
          </Box>
        </OneModal.Footer>
      </OneModal>
    )
  }
)

const Editor = observer(
  ({ contentItem, trainingId }: { contentItem: ContentItem; trainingId: number }) => {
    const EditorComponent = contentRegistry.fetch(contentItem, 'editorComponent')
    return EditorComponent ? (
      <EditorComponent contentItem={contentItem} trainingId={trainingId} />
    ) : null
  }
)
